import React, { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void; // Make onChange optional
  error?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, onChange, placeholder, id, error, ...restProps }, ref) => {
    const [active, setActive] = useState(false);
    const [inputValue, setInputValue] = useState(restProps.value || ""); // Initialize value

    useEffect(() => {
      if (restProps.value) {
        setActive(true);
      }
    }, [restProps.value]);

    const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setActive(true);
      if (restProps.onFocus) {
        restProps.onFocus(event);
      }
    };

    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      if (!inputValue) {
        setActive(false);
      }
      if (restProps.onBlur) {
        restProps.onBlur(event);
      }
    };

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputValue(event.target.value); // Update local state
      if (onChange) {
        onChange(event); // Call the passed onChange function
      }
    };

    const isValueNonEmpty = () => {
      if (typeof inputValue === "string" || Array.isArray(inputValue)) {
        return inputValue.length > 0;
      }
      return inputValue !== null && inputValue !== undefined;
    };

    return (
      <div
        className={cn(
          "relative min-h-[56px] w-full rounded-[8px] flex items-center",
          error
            ? "border border-border-token-error-subtle bg-bg-error-tertiary-default"
            : active
            ? "border-2 border-primary-500 dark:border-primary-300 bg-primary-50 dark:bg-primary-900"
            : isValueNonEmpty()
            ? "border-2 border-neutral-200 dark:border-neutral-200 bg-primary-50 dark:bg-primary-900"
            : "border border-neutral-600 dark:border-neutral-400"
        )}
      >
        <textarea
          id={id} // Ensure textarea has an ID
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange} // Ensure handleChange is passed here
          aria-invalid={error || undefined}
          className={cn(
            className,
            "outline-none min-h-[56px] w-full rounded text-sm transition-all duration-200 ease-in-out bg-transparent px-4 py-2",
            isValueNonEmpty() || active ? "pt-6" : "pt-0"
          )}
          ref={ref}
          {...restProps} // Spread props to ensure any other props like name, id, etc., are passed
        />
        <label
          htmlFor={id} // Associate the label with the textarea
          className={[
            "label-m-medium absolute left-2 transition-all duration-200 ease-in-out pointer-events-none w-[98%] h-4 px-2",
            isValueNonEmpty() || active
              ? "text-xs top-[6px]"
              : "text-sm top-[8px]",
            error
              ? "!text-fg-error-strong"
              : isValueNonEmpty() || active
              ? "!text-primary-500 dark:!text-primary-300"
              : "text-fg-neutral-subtle dark:text-neutral-600",
          ].join(" ")}
        >
          {placeholder}
        </label>
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

export { Textarea };
